/**
 * Core-100 owner direction 12 — technicians who also work as Service Advisors.
 *
 * They are never added to the front-office schedule automatically. A manager adds front-office
 * hours for them here, explicitly (the entry carries `multi_role: true`), and those hours may not
 * overlap the person's bay shift — the database guard refuses it, and the editor says so first.
 * A failed read hides this section; it never invents candidates.
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { mayOptIntoFrontOffice, technicianDayWindow, type TechnicianHoursLike } from '@/lib/staffSchedule/frontOfficeOptIn';

export interface FrontOfficeCandidate {
  user_id: string;
  display_name: string;
  tech: TechnicianHoursLike | null;
}

interface Props {
  shopId: string;
  /** user_ids that already have a schedule entry (opted in or ordinary front office) */
  scheduledUserIds: ReadonlySet<string>;
  onCandidates: (byUser: Map<string, FrontOfficeCandidate>) => void;
  onAdd: (candidate: FrontOfficeCandidate) => void;
}

export async function loadFrontOfficeCandidates(shopId: string): Promise<FrontOfficeCandidate[]> {
  const { data: members, error } = await supabase
    .from('memberships')
    .select('id, user_id, role, membership_work_roles(work_role)')
    .eq('shop_id', shopId)
    .eq('status', 'active');
  if (error || !members) return [];
  const rows = members as unknown as Array<{ user_id: string; role: string; membership_work_roles: Array<{ work_role: string }> | null }>;
  const eligible = rows.filter((m) => mayOptIntoFrontOffice(m.role, (m.membership_work_roles ?? []).map((w) => w.work_role)));
  if (eligible.length === 0) return [];
  const ids = eligible.map((m) => m.user_id);
  const [{ data: profiles }, { data: techs }] = await Promise.all([
    supabase.from('profiles').select('id, display_name').in('id', ids),
    supabase.from('technicians').select('hours, shift_start, shift_end, technician_profiles!inner(user_id)').eq('shop_id', shopId).in('technician_profiles.user_id', ids),
  ]);
  const nameById = new Map(((profiles ?? []) as Array<{ id: string; display_name: string | null }>).map((p) => [p.id, p.display_name]));
  const techByUser = new Map<string, TechnicianHoursLike>();
  for (const t of (techs ?? []) as unknown as Array<TechnicianHoursLike & { technician_profiles: { user_id: string } | null }>) {
    if (t.technician_profiles?.user_id) techByUser.set(t.technician_profiles.user_id, t);
  }
  return eligible.map((m) => ({ user_id: m.user_id, display_name: nameById.get(m.user_id) || 'Unknown', tech: techByUser.get(m.user_id) ?? null }));
}

/** A starting front-office window that does not overlap the bay shift on weekdays (after it, else before it). */
export function suggestFrontOfficeWindow(tech: TechnicianHoursLike | null): { shift_start: string; shift_end: string } {
  const w = tech ? technicianDayWindow(tech, 1) : null;
  const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  if (!w) return { shift_start: '08:00', shift_end: '12:00' };
  if (w.close <= 20 * 60) return { shift_start: hhmm(w.close), shift_end: hhmm(Math.min(w.close + 4 * 60, 24 * 60 - 1)) };
  return { shift_start: hhmm(Math.max(w.open - 4 * 60, 0)), shift_end: hhmm(w.open) };
}

export function FrontOfficeOptInSection({ shopId, scheduledUserIds, onCandidates, onAdd }: Props) {
  const [candidates, setCandidates] = useState<FrontOfficeCandidate[]>([]);

  useEffect(() => {
    let live = true;
    void loadFrontOfficeCandidates(shopId).then((c) => {
      if (!live) return;
      setCandidates(c);
      onCandidates(new Map(c.map((x) => [x.user_id, x])));
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId]);

  const notYet = candidates.filter((c) => !scheduledUserIds.has(c.user_id));
  if (notYet.length === 0) return null;
  return (
    <div className="mt-6 space-y-2" data-testid="front-office-opt-in">
      <h4 className="text-sm font-medium">Technicians who also work as Service Advisors</h4>
      <p className="text-xs text-muted-foreground">
        Not on the front-office schedule unless you add them. Their front-office hours cannot overlap their bay shift —
        an hour counts as front-office or technician time, never both.
      </p>
      {notYet.map((c) => (
        <div key={c.user_id} className="flex items-center justify-between rounded-lg border p-3">
          <span className="text-sm">{c.display_name}</span>
          <Button variant="outline" size="sm" onClick={() => onAdd(c)} data-testid={`add-front-office-${c.user_id}`}>
            Add front-office hours
          </Button>
        </div>
      ))}
    </div>
  );
}
